import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

export interface DropTrailerFlagConfig {
  field: string;
  label: string;
  shortLabel: string;
  tone: 'sky' | 'amber' | 'emerald' | 'rose' | 'violet' | 'cyan' | 'slate';
  valueType?: 'boolean' | 'timestamp';
  readOnly?: boolean;
  dateField?: string;
  timeField?: string;
}

interface DropTrailerFlagDisplayItem extends DropTrailerFlagConfig {
  value: boolean;
  timestamp: string;
}

export interface DropTrailerFlagsCellRendererParams extends ICellRendererParams {
  flagsConfig: DropTrailerFlagConfig[];
  showProgress?: boolean;
  onFlagToggle?: (event: {
    field: string;
    value: boolean;
    flag: DropTrailerFlagConfig;
    data: any;
    node: any;
  }) => void;
  onFlowManage?: (event: {
    data: any;
    node: any;
  }) => void;
}

@Component({
  selector: 'app-drop-trailer-flags-cell-renderer',
  template: `
    <div class="dtfr" [class.dtfr--with-progress]="showProgress">
      <ng-container *ngIf="showProgress; else adminFlagPills">
        <div class="dtfr__flow" [attr.aria-label]="progressTitle">
          <div class="dtfr__flow-meta">
            <span class="dtfr__flow-label">{{ activeStatusLabel }}</span>
            <span class="dtfr__flow-actions">
              <span class="dtfr__progress-count">{{ completedCount }}/{{ flags.length }}</span>
              <button
                type="button"
                class="dtfr__manage"
                aria-label="Manage trailer flow dates and times"
                (click)="onManageFlow($event)"
              >
                <i class="fa-solid fa-pen-to-square"></i>
              </button>
            </span>
          </div>

          <div class="dtfr__segment-bar">
            <button
              *ngFor="let flag of flags; let index = index"
              type="button"
              class="dtfr__segment"
              [ngClass]="segmentClass(index, flag)"
              [attr.aria-label]="getTitle(flag)"
              (click)="onFlagClick(flag, $event)"
            >
              <span class="dtfr__segment-icon" aria-hidden="true">
                <i *ngIf="flag.value" class="fa-solid fa-check"></i>
                <span *ngIf="!flag.value" class="dtfr__segment-number">{{ index + 1 }}</span>
              </span>
              <span class="dtfr__segment-sr">Step {{ index + 1 }}: {{ flag.label }}</span>
            </button>
          </div>
        </div>
      </ng-container>

      <ng-template #adminFlagPills>
        <div class="dtfr__flags">
          <button
            *ngFor="let flag of flags"
            type="button"
            class="dtfr__flag"
            [ngClass]="['dtfr__flag--' + flag.tone, flag.value ? 'is-on' : 'is-off']"
            [attr.aria-label]="getTitle(flag)"
            [disabled]="flag.readOnly"
            (click)="onFlagClick(flag, $event)"
          >
            <span class="dtfr__icon">
              <i class="fa-solid" [ngClass]="flag.value ? 'fa-check' : 'fa-minus'"></i>
            </span>
            <span class="dtfr__label">{{ flag.shortLabel }}</span>
          </button>
        </div>
      </ng-template>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      min-width: 0;
    }

    .dtfr {
      display: flex;
      align-items: center;
      gap: 0.45rem;
      width: 100%;
      min-width: 0;
      overflow: hidden;
    }

    .dtfr__flow {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0.22rem;
      width: 100%;
      height: 100%;
      min-width: 0;
    }

    .dtfr__flow-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.45rem;
      width: 100%;
      min-width: 0;
    }

    .dtfr__flow-label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--dt-text-main, #0f172a);
      font-size: 0.66rem;
      font-weight: 850;
      line-height: 1.05;
    }

    .dtfr__progress-count {
      flex: 0 0 auto;
      color: var(--dt-text-secondary, #64748b);
      font-size: 0.62rem;
      font-weight: 850;
      line-height: 1;
      white-space: nowrap;
    }

    .dtfr__flow-actions {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      flex: 0 0 auto;
    }

    .dtfr__manage {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      height: 1.25rem;
      border: 1px solid rgba(148, 163, 184, 0.28);
      border-radius: 999px;
      background: rgba(248, 250, 252, 0.92);
      color: #64748b;
      cursor: pointer;
      font-size: 0.58rem;
      appearance: none;
      transition: background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease;
    }

    .dtfr__manage:hover,
    .dtfr__manage:focus-visible {
      border-color: #38bdf8;
      background: rgba(224, 242, 254, 0.95);
      color: #0369a1;
      box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.12);
      outline: none;
    }

    .dtfr__segment-bar {
      display: grid;
      grid-template-columns: repeat(8, minmax(0, 1fr));
      gap: 3px;
      width: 100%;
      height: 14px;
      overflow: visible;
      border-radius: 999px;
      background: transparent;
    }

    .dtfr__segment {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      min-width: 0;
      border: 1px solid rgba(203, 213, 225, 0.82);
      border-radius: 999px;
      background: rgba(248, 250, 252, 0.92);
      color: #94a3b8;
      cursor: pointer;
      appearance: none;
      transition: background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease, transform 0.16s ease;
    }

    .dtfr__segment.is-complete {
      border-color: #059669;
      background: #10b981;
      color: #ffffff;
    }

    .dtfr__segment.is-next {
      border-color: #0ea5e9;
      border-style: dashed;
      background: #ffffff;
      color: #0284c7;
      box-shadow: inset 0 0 0 1px rgba(14, 165, 233, 0.12);
    }

    .dtfr__segment:hover,
    .dtfr__segment:focus-visible {
      border-color: #38bdf8;
      background: rgba(224, 242, 254, 0.95);
      color: #0369a1;
      box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.12);
      transform: translateY(-1px);
    }

    .dtfr__segment.is-complete:hover,
    .dtfr__segment.is-complete:focus-visible {
      border-color: #059669;
      background: #059669;
      color: #ffffff;
      box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.18);
    }

    .dtfr__segment:focus-visible {
      outline: none;
    }

    .dtfr__segment-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      font-size: 0.44rem;
      font-weight: 900;
      line-height: 1;
      opacity: 0;
      transition: opacity 0.14s ease;
    }

    .dtfr__segment.is-next .dtfr__segment-icon {
      opacity: 0.7;
    }

    .dtfr__flow:hover .dtfr__segment-icon,
    .dtfr__segment:focus-visible .dtfr__segment-icon {
      opacity: 0.88;
    }

    .dtfr__segment-sr {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .dtfr__flags {
      display: flex;
      align-items: center;
      gap: 0.28rem;
      min-width: 0;
      overflow: hidden;
    }

    .dtfr__flag {
      display: inline-flex;
      align-items: center;
      gap: 0.32rem;
      flex: 0 0 auto;
      height: 24px;
      max-width: 92px;
      padding: 0 0.48rem 0 0.24rem;
      border: 1px solid rgba(148, 163, 184, 0.16);
      border-radius: 999px;
      background: rgba(148, 163, 184, 0.08);
      color: var(--dt-text-secondary, #64748b);
      cursor: pointer;
      font-size: 0.62rem;
      font-weight: 800;
      line-height: 1;
      white-space: nowrap;
      appearance: none;
      transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
    }

    .dtfr__flag:hover {
      border-color: rgba(14, 165, 233, 0.38);
      color: var(--dt-text-main, #0f172a);
    }

    .dtfr__flag:focus-visible {
      outline: 2px solid rgba(14, 165, 233, 0.55);
      outline-offset: 2px;
    }

    .dtfr__flag.is-on {
      color: var(--dt-text-main, #0f172a);
    }

    .dtfr__flag:disabled {
      cursor: default;
      opacity: 1;
    }

    .dtfr__flag.is-on.dtfr__flag--sky {
      background: rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.26);
    }

    .dtfr__flag.is-on.dtfr__flag--amber {
      background: rgba(245, 158, 11, 0.16);
      border-color: rgba(245, 158, 11, 0.28);
    }

    .dtfr__flag.is-on.dtfr__flag--emerald {
      background: rgba(34, 197, 94, 0.14);
      border-color: rgba(34, 197, 94, 0.26);
    }

    .dtfr__flag.is-on.dtfr__flag--rose {
      background: rgba(244, 63, 94, 0.13);
      border-color: rgba(244, 63, 94, 0.24);
    }

    .dtfr__flag.is-on.dtfr__flag--violet {
      background: rgba(139, 92, 246, 0.14);
      border-color: rgba(139, 92, 246, 0.26);
    }

    .dtfr__flag.is-on.dtfr__flag--cyan {
      background: rgba(6, 182, 212, 0.14);
      border-color: rgba(6, 182, 212, 0.26);
    }

    .dtfr__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 0.95rem;
      height: 0.95rem;
      flex: 0 0 auto;
      border-radius: 999px;
      font-size: 0.52rem;
      background: rgba(148, 163, 184, 0.14);
      color: currentColor;
    }

    .dtfr__flag.is-on .dtfr__icon {
      background: #22c55e;
      color: #ffffff;
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.12);
    }

    .dtfr__label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `],
})
export class DropTrailerFlagsCellRendererComponent implements ICellRendererAngularComp {
  flags: DropTrailerFlagDisplayItem[] = [];
  showProgress = false;
  completedCount = 0;
  progressTitle = '';
  activeStatusLabel = '';
  nextStepIndex = -1;
  private params?: DropTrailerFlagsCellRendererParams;

  agInit(params: DropTrailerFlagsCellRendererParams): void {
    this.applyParams(params);
  }

  refresh(params: DropTrailerFlagsCellRendererParams): boolean {
    this.applyParams(params);
    return true;
  }

  getTitle(flag: DropTrailerFlagDisplayItem): string {
    const status = flag.value ? 'Complete' : 'Open';
    const timestamp = flag.timestamp ? ` ${flag.timestamp}.` : '';
    if (flag.readOnly) {
      return `${flag.label}: ${status}.${timestamp}`;
    }
    return `${flag.label}: ${status}.${timestamp} Click to mark ${flag.value ? 'open' : 'complete'}.`;
  }

  onFlagClick(flag: DropTrailerFlagDisplayItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (flag.readOnly) return;

    this.params?.onFlagToggle?.({
      field: flag.field,
      value: !flag.value,
      flag,
      data: this.params.data,
      node: this.params.node,
    });
  }

  onManageFlow(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.params?.onFlowManage?.({
      data: this.params.data,
      node: this.params.node,
    });
  }

  segmentClass(index: number, flag: DropTrailerFlagDisplayItem): string {
    if (flag.value) return 'is-complete';
    if (index === this.nextStepIndex) return 'is-next';
    return 'is-upcoming';
  }

  private applyParams(params: DropTrailerFlagsCellRendererParams): void {
    this.params = params;
    const data = params.data ?? {};
    const configs = params.flagsConfig ?? [];

    this.showProgress = !!params.showProgress;
    this.flags = configs.map((config) => ({
      ...config,
      value: this.flagValue(config, data),
      timestamp: this.flagTimestamp(config, data),
    }));

    this.completedCount = this.flags.filter((flag) => flag.value).length;
    this.nextStepIndex = this.resolveNextStepIndex();
    this.activeStatusLabel = this.resolveActiveStatusLabel();
    this.progressTitle = this.resolveProgressTitle();
  }

  private resolveNextStepIndex(): number {
    const firstOpenIndex = this.flags.findIndex((flag) => !flag.value);
    if (this.completedCount === 0 || firstOpenIndex < 0) return -1;
    return firstOpenIndex;
  }

  private resolveActiveStatusLabel(): string {
    if (!this.flags.length) return 'No trailer flow';
    if (this.completedCount === 0) return 'Not started';
    if (this.completedCount === this.flags.length) return 'Flow complete';

    for (let i = this.flags.length - 1; i >= 0; i--) {
      const flag = this.flags[i];
      if (flag?.value) return flag.label;
    }

    return 'In progress';
  }

  private resolveProgressTitle(): string {
    if (!this.flags.length) return 'No trailer flow configured.';

    const nextLabel = this.nextStepIndex >= 0 ? this.flags[this.nextStepIndex]?.label : '';
    const nextText = nextLabel ? ` Next: ${nextLabel}.` : '';
    return `${this.completedCount} of ${this.flags.length} trailer milestones complete. Current: ${this.activeStatusLabel}.${nextText}`;
  }

  private formatTimestamp(date: any, time: any): string {
    const parts = [date, time]
      .map((part) => (part === null || part === undefined ? '' : String(part).trim()))
      .filter(Boolean);

    return parts.join(' ');
  }

  private flagValue(config: DropTrailerFlagConfig, data: Record<string, any>): boolean {
    const raw = data?.[config.field];
    if (config.valueType === 'timestamp') {
      return raw !== null && raw !== undefined && String(raw).trim() !== '';
    }

    if (this.toBoolean(raw)) {
      return true;
    }

    const atValue = data?.[`${config.field}_at`];
    const dateValue = data?.[config.dateField || ''];
    const timeValue = data?.[config.timeField || ''];
    return !!(this.normalizeDateTime(atValue) || (this.normalizeDate(dateValue) && this.normalizeTime(timeValue)));
  }

  private flagTimestamp(config: DropTrailerFlagConfig, data: Record<string, any>): string {
    if (config.valueType === 'timestamp') {
      return this.formatTimestampValue(data?.[config.field]);
    }

    return this.formatTimestamp(data?.[config.dateField || ''], data?.[config.timeField || '']);
  }

  private normalizeDateTime(value: any): string {
    const date = this.normalizeDate(value);
    const time = this.normalizeTime(value);
    return date && time ? `${date} ${time}:00` : '';
  }

  private normalizeDate(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || '';
  }

  private normalizeTime(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim().match(/(?:^|\s|T)(\d{2}:\d{2})/)?.[1] || '';
  }

  private formatTimestampValue(value: any): string {
    if (value === null || value === undefined) return '';

    const raw = String(value).trim();
    const date = raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || '';
    const time = raw.match(/(?:^|\s|T)(\d{2}:\d{2})/)?.[1] || '';
    return this.formatTimestamp(date, time);
  }

  private toBoolean(raw: any): boolean {
    if (typeof raw === 'string') {
      const value = raw.trim().toLowerCase();
      return value === 'true' || value === 't' || value === '1' || value === 'yes' || value === 'y';
    }

    return raw === true || raw === 1;
  }
}
